/**
 * "What assets do you have?" — the funding-source type picker, shared by guided planning and the D2C
 * self-funding sequence.
 *
 * The host passes everything flow-specific in: the sources to pre-select from (live, or a guided
 * snapshot), the selection it saved last time, loading/saving/read-only state, what to do on
 * continue, and a `frame` that renders the title, cards and Continue action in the host's own chrome.
 * The screen itself never saves anything and never touches funding sources, so partner-shared
 * accounts cannot be changed from here.
 */
import { Skeleton } from '@design-system';

import { FundingSourceCard } from '../components/funding-source-card';
import type { AssetTypeId } from '../config/funding-source-types';
import { useFundingSourceSelection } from '../hooks/use-funding-source-selection';
import type { FundingScreenFrame } from '../types';

export interface FundingSourceSelectionProps {
  /** Self-funding sources pre-selection is based on (live data, or a guided snapshot). */
  existingSelfFundingSources: Record<string, SelfFundingSource> | undefined;
  /** The selection the host saved last time, or null when nothing was saved. */
  savedSelection: readonly AssetTypeId[] | null;
  /** True while the host is still loading the client or its saved selection. */
  isLoading: boolean;
  readOnly: boolean;
  isSaving: boolean;
  onContinue: (selection: AssetTypeId[]) => void | Promise<void>;
  frame: FundingScreenFrame;
}

const SKELETON_CARD_COUNT = 6;

export function FundingSourceSelection({
  existingSelfFundingSources,
  savedSelection,
  isLoading,
  readOnly,
  isSaving,
  onContinue,
  frame,
}: FundingSourceSelectionProps) {
  const { typesToShow, selected, toggle, hasSelection } = useFundingSourceSelection(
    existingSelfFundingSources,
    savedSelection,
  );

  const content = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {isLoading
        ? Array.from({ length: SKELETON_CARD_COUNT }, (_, i) => (
            <Skeleton
              key={i}
              className="h-[60px] rounded-xl"
            />
          ))
        : typesToShow.map(type => (
            <FundingSourceCard
              key={type.id}
              id={type.id}
              label={type.label}
              isSelected={selected.has(type.id)}
              onToggle={toggle}
              disabled={readOnly}
            />
          ))}
    </div>
  );

  return frame({
    title: 'What assets do you have?',
    subtitle:
      "Select all the accounts and assets you currently own. We'll use this to assess your long-term care coverage capacity.",
    content,
    primaryAction: {
      label: 'Continue',
      onSelect: () => onContinue(Array.from(selected)),
      disabled: !hasSelection || isSaving || isLoading,
      busy: isSaving,
    },
  });
}
